import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { IdeaListItem, Paginated } from '@shared/types';
import { PageContainer, PageHeader } from '../../app/AppShell';
import { Button } from '../../components/Button';
import { Card, CardBody, EmptyState, Spinner } from '../../components/Card';
import { TabBar } from '../../components/Tabs';
import { api, errorMessage } from '../../lib/api';
import { IdeaCard } from './components/IdeaCard';

type Scope = 'authored' | 'involved' | 'assigned';

const SCOPE_LABELS: Record<Scope, { label: string; hint: string }> = {
  authored: { label: '我提的', hint: '包含草稿与归档' },
  involved: { label: '我参与的', hint: '评论过、点赞过或关注过的' },
  assigned: { label: '我认领的', hint: '我是负责人的创意' },
};

/**
 * 「我的」视图。
 *
 * 这就是本平台对「通知」的替代方案：不做消息中心、不做 @ 提及推送，
 * 只把与我有关的创意列出来，有新动态的那条带一个未读点。
 */
export function MyIdeasPage() {
  const [scope, setScope] = useState<Scope>('authored');
  const [includeArchived, setIncludeArchived] = useState(false);
  const [items, setItems] = useState<IdeaListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await api.get<Paginated<IdeaListItem>>('/api/ideas/mine', {
        query: { scope, includeArchived: includeArchived ? 1 : undefined, pageSize: 200 },
      });
      setItems(result.items);
      setError(null);
    } catch (fetchError) {
      setError(errorMessage(fetchError));
    } finally {
      setLoading(false);
    }
  }, [scope, includeArchived]);

  useEffect(() => {
    void load();
  }, [load]);

  const unread = items.filter((item) => item.viewer.unread).length;

  return (
    <PageContainer>
      <PageHeader
        title="我的"
        description="我提的、我参与的、我认领的。有未读动态的创意会带一个圆点标记。"
        actions={
          <Link to="/ideas/new">
            <Button variant="primary">提出创意</Button>
          </Link>
        }
      />

      <Card>
        <CardBody className="flex flex-wrap items-center gap-3">
          <TabBar
            size="md"
            value={scope}
            onChange={(value) => setScope(value as Scope)}
            items={(Object.keys(SCOPE_LABELS) as Scope[]).map((key) => ({ key, label: SCOPE_LABELS[key].label, hint: SCOPE_LABELS[key].hint }))}
          />
          <div className="ml-auto flex items-center gap-2">
            {unread > 0 ? <span className="text-[11px] text-accent">{unread} 条有新动态</span> : null}
            <Button size="sm" variant={includeArchived ? 'default' : 'ghost'} onClick={() => setIncludeArchived((value) => !value)}>
              {includeArchived ? '隐藏归档' : '包含归档'}
            </Button>
            <Button size="sm" variant="ghost" loading={loading} onClick={() => void load()}>
              刷新
            </Button>
          </div>
        </CardBody>
      </Card>

      {error ? <p className="text-xs text-critical">{error}</p> : null}

      {loading && items.length === 0 ? (
        <div className="flex items-center gap-2 p-6 text-sm text-fg-muted">
          <Spinner /> 正在加载…
        </div>
      ) : items.length === 0 ? (
        <Card>
          <CardBody>
            <EmptyState
              title={`${SCOPE_LABELS[scope].label}：暂无内容`}
              description={
                scope === 'authored'
                  ? '你还没有提出过创意。挑一个具体的问题，写清核心假设与成功指标就够了。'
                  : '当你在创意下评论、点赞或认领后，它会出现在这里。'
              }
              action={
                <Link to="/ideas/new">
                  <Button variant="primary">提出第一个创意</Button>
                </Link>
              }
            />
          </CardBody>
        </Card>
      ) : (
        <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
          {items.map((item) => (
            <IdeaCard key={item.id} idea={item} showStatus />
          ))}
        </div>
      )}
    </PageContainer>
  );
}
